import React, { useState, useEffect } from 'react';
import { fetchTickets, fetchAlerts, fetchTodayAlerts, fetchHoras, fetchSLAAlerts } from '../../api';
import { getMexicoMonthRange, formatMexicoDateTime } from '../../dateUtils';
import { Search, Filter, Moon, MoonStar, RefreshCw } from 'lucide-react';
import KPICards from '../../components/shared/KPICards';
import StatusChart from '../../components/shared/StatusChart';
import PriorityChart from '../../components/shared/PriorityChart';
import AlertsTrend from '../../components/shared/AlertsTrend';
import TicketsTrend from '../../components/shared/TicketsTrend';
import TicketsTable from '../../components/shared/TicketsTable';
import AlertsGrid from '../../components/shared/AlertsGrid';
import HoursByClientTable from '../../components/shared/HoursByClientTable';
import DateFilterComponent from '../../components/shared/DateFilterComponent';
import SLABurnedTicketsDomo from '../../components/shared/SLABurnedTicketsDomo';
import SLAAlertsPanel from '../../components/shared/SLAAlertsPanel';
import SLAAlertsTable from '../../components/shared/SLAAlertsTable';

export default function KloudOpsGerenteDashboard() {
  const [tickets, setTickets] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [tdalerts, setTodayAlerts] = useState([]);
  const [horas, setHoras] = useState([]);
  const [slaAlerts, setSlaAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [cloudFilter, setCloudFilter] = useState([]); // Filtro de nubes para alertas
  const [searchTerm, setSearchTerm] = useState('');
  const [openFilterMenu, setOpenFilterMenu] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  const [globalDateFilter, setGlobalDateFilter] = useState({
    type: 'absolute',
    ...getMexicoMonthRange()
  });

  const todaysAlertsGrouped = tdalerts
    .reduce((grouped, alert) => {
      const key = `${alert.Alerta}-${alert.Nube}`;
      
      if (grouped[key]) {
        grouped[key].Cantidad += alert.Cantidad;
        if (alert.Hora > grouped[key].Hora) {
          grouped[key].Hora = alert.Hora;
        }
        if (!grouped[key].horas) {
          grouped[key].horas = [grouped[key].Hora];
        }
        grouped[key].horas.push(alert.Hora);
      } else {
        grouped[key] = {
          ...alert,
          horas: [alert.Hora]
        };
      }
      
      return grouped;
    }, {});

  const todaysAlerts = Object.values(todaysAlertsGrouped)
    .sort((a, b) => b.Cantidad - a.Cantidad);
  
  // Obtener nubes únicas de las alertas
  const nubesDisponibles = [...new Set(todaysAlerts.map(a => a.Nube).filter(Boolean))].sort();
  
  // Filtrar alertas por nube
  const filteredAlerts = cloudFilter.length === 0 
    ? todaysAlerts 
    : todaysAlerts.filter(alert => cloudFilter.includes(alert.Nube));

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [ticketsData, alertsData, alertsTodayData, horasData, slaAlertsData] = await Promise.all([
          fetchTickets(globalDateFilter),
          fetchAlerts(globalDateFilter),
          fetchTodayAlerts(),
          fetchHoras(globalDateFilter),
          fetchSLAAlerts(24) // Últimas 24 horas
        ]);
        setTickets(ticketsData);
        setAlerts(alertsData);
        setTodayAlerts(alertsTodayData);
        setHoras(horasData);
        
        // Filtrar alertas de OPS (excluir Home Depot que es SaaS)
        const opsAlerts = slaAlertsData.filter(alert => 
          alert.destinatario_chat === 'OPS' || 
          (alert.cuenta_cliente !== 'Home Depot' && !alert.cuenta_cliente?.includes('Home Depot'))
        );
        setSlaAlerts(opsAlerts);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    
    const interval = setInterval(loadData, 180000); // 3 minutos
    return () => clearInterval(interval);
  }, [globalDateFilter]);

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
      } else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlHeader);
    
    return () => {
      window.removeEventListener('scroll', controlHeader);
    };
  }, [lastScrollY]);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      const [ticketsData, alertsData, alertsTodayData, horasData] = await Promise.all([
        fetchTickets(globalDateFilter),
        fetchAlerts(globalDateFilter),
        fetchTodayAlerts(),
        fetchHoras(globalDateFilter)
      ]);
      setTickets(ticketsData);
      setAlerts(alertsData);
      setTodayAlerts(alertsTodayData);
      setHoras(horasData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleDateFilterChange = (newDateFilter) => {
    setGlobalDateFilter(newDateFilter);
  };

  const getDateFilterLabel = () => {
    if (globalDateFilter.type === 'absolute') {
      return `Del ${globalDateFilter.startDate} al ${globalDateFilter.endDate}`;
    } else {
      const unitLabels = {
        minutes: 'minutos',
        hours: 'horas',
        days: 'días',
        weeks: 'semanas',
        months: 'meses'
      };
      const unitLabel = unitLabels[globalDateFilter.unit] || globalDateFilter.unit;
      return `Últimos ${globalDateFilter.amount} ${unitLabel}`;
    }
  };

  // Estados a excluir
  const estadosExcluidos = ['Solucionado', 'Cerrado', 'Cancelado'];
  
  // Obtener estados únicos de los tickets actuales (dinámico)
  const estadosDisponibles = [...new Set(
    tickets
      .map(t => t.Estado_de_solicitud)
      .filter(Boolean)
  )].sort();
  
  // Obtener prioridades únicas de los tickets actuales (dinámico)
  const prioridadesEnTickets = [...new Set(
    tickets
      .map(t => t.Prioridad)
      .filter(Boolean)
  )];
  
  // Asegurar que siempre estén disponibles las prioridades estándar
  const prioridadesEstandar = ['Crítica', 'Alta', 'Media', 'Baja'];
  const prioridadesDisponibles = [...new Set([...prioridadesEstandar, ...prioridadesEnTickets])]
    .sort((a, b) => {
      const orden = { 'Crítica': 0, 'Alta': 1, 'Media': 2, 'Baja': 3 };
      return (orden[a] || 99) - (orden[b] || 99);
    });
  
  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.Asunto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.Cuenta?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtro de prioridad: si no hay selección, mostrar todas
    const matchesPriority = priorityFilter.length === 0 || priorityFilter.includes(ticket.Prioridad);
    
    // Filtro de estado: si no hay selección, excluir solo los cerrados
    const matchesStatus = statusFilter.length === 0 
      ? !estadosExcluidos.includes(ticket.Estado_de_solicitud)
      : statusFilter.includes(ticket.Estado_de_solicitud);
    
    return matchesSearch && matchesPriority && matchesStatus;
  });

  const themeClasses = isDarkMode 
    ? 'bg-[radial-gradient(circle_at_top_right,rgba(124,58,237,0.22),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(34,211,238,0.12),transparent_25%),linear-gradient(135deg,#140728_0%,#0b041a_50%,#1a0b36_100%)] text-white'
    : 'bg-[radial-gradient(circle_at_top_right,rgba(139,92,246,0.16),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.10),transparent_24%),linear-gradient(135deg,#ebe4ff_0%,#f6f2ff_42%,#edf4ff_100%)] text-slate-900';

  const surfaceClasses = isDarkMode
    ? 'border border-violet-500/25 bg-[#1a0d36]/70 backdrop-blur-md shadow-[0_18px_60px_rgba(0,0,0,0.24)]'
    : 'border border-violet-300/75 bg-white/94 text-slate-900 backdrop-blur-md shadow-[0_18px_45px_rgba(76,29,149,0.12)]';

  const cardClasses = surfaceClasses;
  const panelClasses = `${surfaceClasses} rounded-2xl`;
  const headerLabelClasses = isDarkMode ? 'text-violet-200/75' : 'text-violet-800/80';
  const titleClasses = isDarkMode ? 'text-violet-100' : 'text-slate-950';
  const headerMetaClasses = isDarkMode ? 'border-violet-400/30 bg-black/20 text-violet-100/85' : 'border-violet-300 bg-violet-50/95 text-violet-950';
  const fieldClasses = isDarkMode
    ? 'bg-black/20 border-violet-400/30 text-violet-50 placeholder:text-violet-200/45'
    : 'bg-white border-violet-300 text-slate-950 placeholder:text-slate-500';
  const floatingButtonClasses = isDarkMode
    ? 'border-violet-300/40 bg-violet-500/20 text-violet-50 hover:bg-violet-500/35'
    : 'border-violet-300 bg-white text-violet-950 hover:bg-violet-50';
  const ghostButtonClasses = isDarkMode
    ? 'text-violet-300 hover:text-violet-100'
    : 'text-violet-800 hover:text-violet-950';
  const counterBadgeClasses = isDarkMode
    ? 'bg-violet-500 text-white'
    : 'bg-violet-600 text-white';
  const checkboxClasses = isDarkMode
    ? 'border-violet-400/40 bg-black/20 text-cyan-400 focus:ring-cyan-400'
    : 'border-violet-300 bg-white text-violet-600 focus:ring-violet-400';
  const multiSelectClasses = isDarkMode
    ? 'border-violet-400/40 bg-[#120823] text-violet-50'
    : 'border-violet-200 bg-white text-slate-900';

  if (loading) {
    return (
      <div className={`min-h-screen ${themeClasses} flex items-center justify-center`}>
        <div className="flex items-center space-x-4">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-violet-400"></div>
          <span className="text-xl font-semibold">Cargando dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${themeClasses} px-4 pb-8 pt-4 font-sans transition-all duration-300 md:px-6 xl:px-8`}>
      {/* Header */}
      <div 
        className={`sticky top-0 z-30 transition-transform duration-300 ${
          isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
        onMouseEnter={() => setIsHeaderVisible(true)}
      >
        <div className={`${panelClasses} px-5 py-4 md:px-6`}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className={`text-xs font-semibold uppercase tracking-[0.24em] ${headerLabelClasses}`}>
                Kloud Ops / Vista Gerencial
              </p>
              <h1 className={`mt-2 text-3xl font-bold ${titleClasses}`}>
                Dashboard Ejecutivo
              </h1>
              <p className={`mt-1 text-sm ${headerLabelClasses}`}>
                Métricas estratégicas, tendencias operativas y visibilidad de SLA.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <span className={`rounded-xl border px-3 py-2 text-xs font-medium ${headerMetaClasses}`}>
                {getDateFilterLabel()}
              </span>
              <DateFilterComponent
                onDateFilterChange={handleDateFilterChange}
                cardClasses={cardClasses}
                isDarkMode={isDarkMode}
                currentFilter={globalDateFilter}
              />
              <button
                onClick={handleManualRefresh}
                disabled={refreshing}
                className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${floatingButtonClasses}`}
                title="Actualizar datos"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                Actualizar
              </button>

              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition-all ${floatingButtonClasses}`}
                title="Cambiar tema"
              >
                {isDarkMode ? <Moon className="h-4 w-4" /> : <MoonStar className="h-4 w-4" />}
                {isDarkMode ? 'Modo oscuro' : 'Modo claro'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="pt-6">
        {/* Panel de Alertas de SLA */}
        {slaAlerts.length > 0 && (
          <SLAAlertsPanel alerts={slaAlerts} cardClasses={cardClasses} />
        )}

        <KPICards tickets={tickets} alerts={alerts} cardClasses={cardClasses} dateFilter={getDateFilterLabel()} />
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <StatusChart tickets={tickets} cardClasses={cardClasses} isDarkMode={isDarkMode} />
          <PriorityChart tickets={tickets} cardClasses={cardClasses} isDarkMode={isDarkMode} />
        </div>

        {/* Tabla de SLA DOMO - Movida después de los gráficos */}
        <SLABurnedTicketsDomo tickets={tickets} cardClasses={cardClasses} />

        <AlertsTrend alerts={alerts} cardClasses={cardClasses} isDarkMode={isDarkMode} dateFilter={getDateFilterLabel()}/>
        <TicketsTrend tickets={tickets} cardClasses={cardClasses} isDarkMode={isDarkMode} dateFilter={getDateFilterLabel()} />

        {/* Tabla de Alertas de SLA */}
        {slaAlerts.length > 0 && (
          <SLAAlertsTable alerts={slaAlerts} cardClasses={cardClasses} />
        )}

        {/* Filtros de tickets */}
        <div className={`${panelClasses} relative mb-8 p-6 ${openFilterMenu ? 'z-40 overflow-visible' : ''}`}>
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-lg font-semibold">Filtros de Tickets</h3>
              <p className={`mt-1 text-xs ${headerLabelClasses}`}>Usa Ctrl/Cmd + Click para seleccionar múltiples opciones</p>
            </div>
            {(priorityFilter.length > 0 || statusFilter.length > 0) && (
              <button
                onClick={() => {
                  setPriorityFilter([]);
                  setStatusFilter([]);
                  setOpenFilterMenu(null);
                }}
                className={`text-sm transition-colors ${ghostButtonClasses}`}
              >
                Limpiar todos los filtros
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2 flex-1 min-w-[250px]">
              <Search className="w-5 h-5 opacity-70" />
              <input
                type="text"
                placeholder="Buscar por asunto o cuenta..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`flex-1 rounded-xl border px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-violet-400 ${fieldClasses}`}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 opacity-70" />
              <div className={`relative ${openFilterMenu === 'priority' ? 'z-20' : ''}`}>
                <button
                  type="button"
                  onClick={() => setOpenFilterMenu(openFilterMenu === 'priority' ? null : 'priority')}
                  className={`min-w-[180px] rounded-xl border px-4 py-2 text-left ${fieldClasses}`}
                >
                  <span className="text-sm">
                    {priorityFilter.length === 0 
                      ? 'Prioridades' 
                      : `${priorityFilter.length} seleccionada${priorityFilter.length > 1 ? 's' : ''}`
                    }
                  </span>
                </button>
                {openFilterMenu === 'priority' && (
                  <select
                    multiple
                    size={Math.max(4, Math.min(prioridadesDisponibles.length, 6))}
                    value={priorityFilter}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setPriorityFilter(selected);
                    }}
                    onBlur={() => setOpenFilterMenu(null)}
                    className={`absolute left-0 top-full mt-2 w-full min-w-[180px] rounded-xl border px-3 py-2 shadow-2xl focus:ring-2 focus:ring-violet-400 ${multiSelectClasses}`}
                    style={{ backgroundColor: isDarkMode ? '#120823' : '#ffffff', color: isDarkMode ? '#f5f3ff' : '#0f172a' }}
                    title="Click para seleccionar (Ctrl/Cmd para múltiples)"
                    autoFocus
                  >
                    {prioridadesDisponibles.map(prioridad => (
                      <option
                        key={prioridad}
                        value={prioridad}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setPriorityFilter(prev => prev.includes(prioridad) ? prev.filter(p => p !== prioridad) : [...prev, prioridad]);
                        }}
                      >
                        {priorityFilter.includes(prioridad) ? '✓ ' : '○ '}{prioridad}
                      </option>
                    ))}
                  </select>
                )}
                {priorityFilter.length > 0 && (
                  <span className={`absolute -right-2 -top-2 z-10 flex h-5 w-5 items-center justify-center rounded-full text-xs ${counterBadgeClasses}`}>
                    {priorityFilter.length}
                  </span>
                )}
              </div>
              {priorityFilter.length > 0 && (
                <button
                  onClick={() => setPriorityFilter([])}
                  className={`rounded-lg px-2 py-1 text-xs transition-colors ${panelClasses} hover:bg-red-500/20`}
                  title="Limpiar filtro de prioridades"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <div className={`relative ${openFilterMenu === 'status' ? 'z-20' : ''}`}>
                <button
                  type="button"
                  onClick={() => setOpenFilterMenu(openFilterMenu === 'status' ? null : 'status')}
                  className={`min-w-[200px] rounded-xl border px-4 py-2 text-left ${fieldClasses}`}
                >
                  <span className="text-sm">
                    {statusFilter.length === 0 
                      ? 'Estados (activos)' 
                      : `${statusFilter.length} seleccionado${statusFilter.length > 1 ? 's' : ''}`
                    }
                  </span>
                </button>
                {openFilterMenu === 'status' && (
                  <select
                    multiple
                    size={Math.max(4, Math.min(estadosDisponibles.length, 6))}
                    value={statusFilter}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setStatusFilter(selected);
                    }}
                    onBlur={() => setOpenFilterMenu(null)}
                    className={`absolute left-0 top-full mt-2 w-full min-w-[200px] rounded-xl border px-3 py-2 shadow-2xl focus:ring-2 focus:ring-violet-400 ${multiSelectClasses}`}
                    style={{ backgroundColor: isDarkMode ? '#120823' : '#ffffff', color: isDarkMode ? '#f5f3ff' : '#0f172a' }}
                    title="Click para seleccionar (Ctrl/Cmd para múltiples)"
                    autoFocus
                  >
                    {estadosDisponibles.map(estado => (
                      <option
                        key={estado}
                        value={estado}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setStatusFilter(prev => prev.includes(estado) ? prev.filter(s => s !== estado) : [...prev, estado]);
                        }}
                      >
                        {statusFilter.includes(estado) ? '✓ ' : '○ '}{estado}
                      </option>
                    ))}
                  </select>
                )}
                {statusFilter.length > 0 && (
                  <span className={`absolute -right-2 -top-2 z-10 flex h-5 w-5 items-center justify-center rounded-full text-xs ${counterBadgeClasses}`}>
                    {statusFilter.length}
                  </span>
                )}
              </div>
              {statusFilter.length > 0 && (
                <button
                  onClick={() => setStatusFilter([])}
                  className={`rounded-lg px-2 py-1 text-xs transition-colors ${panelClasses} hover:bg-red-500/20`}
                  title="Limpiar filtro de estados"
                >
                  ✕
                </button>
              )}
            </div>

            <div className={`ml-auto rounded-xl px-3 py-2 ${panelClasses}`}>
              <span className="text-sm font-medium">
                {filteredTickets.length} tickets
              </span>
            </div>
          </div>
        </div>

        <TicketsTable tickets={filteredTickets} cardClasses={cardClasses} />
        
        {/* Alertas del día con filtro por nube */}
        {todaysAlerts.length > 0 && (
          <div className="mb-8">
            <div className={`${panelClasses} p-6`}>
              <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <span>Alertas Activas del Día</span>
                </h2>
                
                {/* Filtro por nube */}
                {nubesDisponibles.length > 1 && (
                  <div className="flex items-center space-x-3">
                    <span className="text-sm opacity-70">Filtrar por nube:</span>
                    {nubesDisponibles.map(nube => {
                      const isChecked = cloudFilter.length === 0 || cloudFilter.includes(nube);
                      
                      return (
                        <label key={nube} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              if (cloudFilter.length === 0) {
                                const otrasNubes = nubesDisponibles.filter(n => n !== nube);
                                setCloudFilter(otrasNubes);
                              } else if (cloudFilter.includes(nube)) {
                                const newFilter = cloudFilter.filter(c => c !== nube);
                                setCloudFilter(newFilter);
                              } else {
                                setCloudFilter([...cloudFilter, nube]);
                              }
                            }}
                            className={`h-4 w-4 rounded ${checkboxClasses}`}
                          />
                          <span className="text-sm font-medium">{nube}</span>
                        </label>
                      );
                    })}
                    {cloudFilter.length > 0 && cloudFilter.length < nubesDisponibles.length && (
                      <button
                        onClick={() => setCloudFilter([])}
                        className={`ml-2 text-xs ${ghostButtonClasses}`}
                      >
                        Ver todas
                      </button>
                    )}
                  </div>
                )}
              </div>
              <AlertsGrid alerts={filteredAlerts} cardClasses={cardClasses} isDarkMode={isDarkMode} />
            </div>
          </div>
        )}
        
        <HoursByClientTable horas={horas} cardClasses={cardClasses} />

                <div className={`mt-8 text-center ${headerLabelClasses}`}>
          <p className="text-sm">
            Dashboard actualizado en tiempo real • {getDateFilterLabel()}
            <br />
            <span className="text-xs">
              Última actualización (CDMX): {formatMexicoDateTime(new Date())}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
